import { useMemo } from 'react';
import { CURSOR_PALETTE } from '../styles/tokens';
import type { AwarenessState, AwarenessUser } from './types';
import { initialsFor } from './types';

interface Props {
  localUser: AwarenessUser;
  peers: Map<number, AwarenessState>;
}

const MAX_VISIBLE = 5;

// Floating chip strip — top-right corner of the canvas column. Shows up to
// MAX_VISIBLE avatars (own first, then peers); overflow rolls into "+N".
// On hover the avatar reveals the full name as a tooltip.
export function PresenceStrip({ localUser, peers }: Props) {
  const list = useMemo(() => {
    const arr: AwarenessUser[] = [localUser];
    peers.forEach((state) => arr.push(state.user));
    return arr;
  }, [localUser, peers]);

  const visible = list.slice(0, MAX_VISIBLE);
  const overflow = list.length - visible.length;

  return (
    <div
      className="absolute top-3 right-3 z-[3] flex items-center -space-x-1.5"
      role="list"
      aria-label="Connected participants"
    >
      {visible.map((u, idx) => (
        <Avatar
          key={u.id}
          user={u}
          isLocal={idx === 0}
          stackIndex={idx}
        />
      ))}
      {overflow > 0 && (
        <div
          className="
            relative w-7 h-7 rounded-full
            bg-bg-panel text-fg-secondary
            border-2 border-bg-app
            font-mono text-12 tabular-nums
            inline-flex items-center justify-center
          "
          title={`${overflow} more`}
          style={{ zIndex: 0 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

function Avatar({
  user,
  isLocal,
  stackIndex,
}: {
  user: AwarenessUser;
  isLocal: boolean;
  stackIndex: number;
}) {
  const color = CURSOR_PALETTE[user.colorIndex] ?? CURSOR_PALETTE[0]!;
  return (
    <div
      role="listitem"
      title={isLocal ? `${user.name} (you)` : user.name}
      className="
        relative w-7 h-7 rounded-full
        border-2 border-bg-app
        text-white text-11 font-medium
        inline-flex items-center justify-center
        shadow-sm
      "
      style={{ background: color, zIndex: 10 - stackIndex }}
    >
      {initialsFor(user.name)}
    </div>
  );
}
